<template>
    <v-data-table
        v-bind="$attrs" v-on="$listeners"
        :items="items_local"
        :headers="display_headers"
        :options.sync="options"
        :server-items-length="server_items_length"
        :search="search"
        :loading="loading"
    >
        <template v-for="(index, name) in $scopedSlots" v-slot:[name]="data">
            <slot :name="name" v-bind="data"></slot>
        </template>
    </v-data-table>
</template>

<script>

// This component was designed to be a wrapper around Vuetify's `v-data-table`
// Its goal is to add a bit of smarts to it, so that it's able to retrieve the proper data from a server, and update itself

// Since it wraps around `v-data-table` however, the official documentation will serve as a guide for using this.
// https://vuetifyjs.com/en/api/v-data-table/
// Any of the props/classes/events/slots you might have used on the classic table will work here also

import _ from "lodash";

export default {
    name: "SmartTable",
    inheritAttrs: false,
    props: {
        'items': {
            // This will be used for the table, and no further data will be fetched
            type: Array,
            required: false,
            default: function () {
                return undefined
            }
        },
        'headers': {
            // This should be an array of objects that define how the table headers and columns are defined
            type: Array,
            required: true,
            default: function () {
                return undefined
            }
        },
        'endpoint': {
            // This is REQUIRED, but only if no items prop was provided
            // Type could be a string URI or a ziggy route name
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'endpoint_method': {
            // This determines the verb/method used for the http request
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'export_endpoint': {
            // This is required only when export functionality is needed
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'export_endpoint_method': {
            // This determines the verb/method used for the export's http request
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'export_filename': {
            // This determines the name of the file that gets exported
            type: String,
            required: false,
            default: function () {
                return 'export.xls'
            }
        },
        'pdf_endpoint': {
            // This is required only when pdf functionality is needed
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'pdf_endpoint_method': {
            // This determines the verb/method used for the pdf's http request
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'excel_endpoint': {
            // This is required only when pdf functionality is needed
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'excel_endpoint_method': {
            // This determines the verb/method used for the pdf's http request
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'search': {
            // This determines the search term
            type: String,
            required: false,
            default: function () {
                return undefined
            }
        },
        'display_fields': {
            // When this is provided, the list of headers will be filtered to match only the fields specified in this array
            // This exists mainly so that we can take advantage of searches defined on headers which we don't necessarily want displayed at the current moment
            type: Array,
            required: false,
            default: function () {
                return undefined
            }
        },
        'infinite_scroll': {
            // This determines if the infinite scroll is enabled
            type: Boolean,
            required: false,
            default: function () {
                return false
            }
        },
        'request_payload_type': {
            // Can determine how the payload is to be sent to the server, defaults to Spatie Query Builder format
            type: String,
            required: false,
            default: function () {
                return 'spatie'     // In ('homebrew', 'spatie')
            }
        },
        'appends': {
            // This should be an array of objects that define how the table headers and columns are defined
            type: Array,
            required: false,
            default: function () {
                return undefined
            }
        },
        'options_payload': {
            // Extra data to be sent to the server when requesting data
            type: Object,
            required: false,
            default: function () {
                return null
            }
        },
        'auto_update': {
            // Will not retrieve new data (server-side AJAX call) unless this is true
            type: Boolean,
            required: false,
            default: function () {
                return true
            }
        },
    },
    data() {
        return {
            items_local: this.items || [],                      // An array of data.  Each element represents one row to be drawn into the data table
            ziggy_route: undefined,
            endpoint_uri: undefined,
            endpoint_method_local: undefined,                   // The locally mutatable endpoint method variable
            options: {},                                        // This contains information about the number of rows per page, what page we're on, sort info etc.  This should be syncd with the options from the actual v-data-table
            hide_default_footer: null,                          // Used to automatically hide the default table footer when in infinite scroll mode
            loading: false,                                     // Indicates whether AJAX call is still in progress
            found_count: 0,                                     // The total number of records matching your current search criteria
            draw: 0,                                            // The number of times we've requested data for the table
            awaiting_search: false,                             // Indicates if the user is still typing in their search criteria
            end_of_data: false,                                 // Can signify that there is no more data available to fetch
            summary_list: false
        }
    },
    computed: {
        is_server_side() {
            return typeof this.items === 'undefined'
        },
        server_items_length () {
            return this.is_server_side ? this.found_count : undefined
        },
        is_ziggy_route() {
            // If the `endpoint` prop has been provided as a ziggy route (instead of just by a plain URI)
            return route().check(this.endpoint)
            // return !!(this.endpoint?.urlBuilder?.route?.methods?.length)
        },
        display_headers() {
            return !this.display_fields ? this.headers : this.headers.filter(header => this.display_fields.includes(header?.value))
        },
    },

    created() {
        this.getData = _.debounce(this.getData, 666) // Replace this function with a debounced version of itself
    },
    methods: {
        async getData() {
            if (!this.is_server_side){
                return
            }
            if (!this.auto_update){
                return
            }
            try {
                this.ajaxPrerequisiteCheck()
                // Initialize state

                if (!this.loading) {
                    this.draw++
                    this.loading = true
                    let response = null

                    // Make AJAX call using appropriate method, assign results to response
                    if (this.endpoint_method_local === 'POST') {
                        response = await axios.post(this.endpoint_uri, this.tableState())        // Syntax is nicer when written for POST
                    } else {
                        response = await axios.get(this.endpoint_uri, {params: this.tableState()})  // Has to be written kind of awkwardly for GET
                    }

                    // Ensure that we only update state with data received from the latest call, and again update state
                    if (response.data.draw === this.draw) {
                        this.loading = false
                        this.addDataToTable(response.data)
                        this.$emit('fetch-complete', response)
                    }
                    else (
                        console.log("Discarding payload, not a draw match")
                    )
                }

            }
            catch (error) {
                console.log(error)
            }

        },
        async getExport() {
            if (!this.export_endpoint) {
                console.error('No export_endpoint was specified')
                return false
            }
            let ajax_uri = route().check(this.export_endpoint) ? route(this.export_endpoint).url() : this.export_endpoint
            let ajax_method = this.export_endpoint_method || 'post'

            // Get the actual export from the server using AJAX -- specifying the responseType here is key
            let blob = await axios({
                url: ajax_uri,
                method: ajax_method,
                responseType: 'blob',
                data: this.tableState(),
            })

            let uri = URL.createObjectURL(blob.data)
            // Easy way to download a file, doesn't allow you to specify filename though
            // window.open(uri, '_blank').focus()

            // Create a new <a> tag that we're going to auto-click
            let downloadLink = document.createElement("a")
            downloadLink.href = uri
            downloadLink.download = this.export_filename
            downloadLink.click()
        },
        async getPdf() {
            if (!this.pdf_endpoint) {
                console.error('No pdf_endpoint was specified')
                return false
            }
            let payload = this.tableState()
            let ajax_uri = route().check(this.pdf_endpoint) ? route(this.pdf_endpoint).url() : this.pdf_endpoint
            let ajax_method = this.pdf_endpoint_method || 'post'

            axios({
                url: ajax_uri,
                method: ajax_method,
                data: payload,
            })
                .then(response => {
                    if (response.data.success) {
                        this.$root.snackbar.success(`You will receive an email to ${this.$page.user?.email} with the PDF once it is ready.`)
                    } else {
                        throw (response.data.error)
                    }
                })
                .catch((error) => {
                    this.$root.snackbar.error(error.response.data.message)
                })
        },
        async getExcel(item) {
            if (!this.excel_endpoint) {
                console.error('No excel_endpoint was specified')
                return false
            }
            let payload = this.tableState()
            let ajax_uri = route().check(this.excel_endpoint) ? route(this.excel_endpoint).url() : this.excel_endpoint
            let ajax_method = this.excel_endpoint_method || 'post'

            axios({
                method: ajax_method,
                url: ajax_uri,
                data: payload,
                onUploadProgress: (evt) => {
                    // this.exportLoading = !this.exportLoading
                },
                onDownloadProgress: (evt) => {
                    // this.exportLoading = !this.exportLoading
                },
            })
                .then(response => {
                    if (response.data.success) {
                        this.$root.snackbar.success(`You will receive an email to ${this.$page.user?.email} with the export once it is ready.`)
                        this.$emit('successful-excel-export')
                    } else {
                        throw (response.data.error)
                    }
                })
                .catch((error) => {
                    this.$root.snackbar.error(error.response?.data.message || error)
                })
        },
        checkForInfiniteScroll() {
            // Infinite scroll currently only works when loading data server-side
            this.infinite_scroll = this.is_server_side ? this.infinite_scroll : false
            // Hide the default footers when infinite scroll is enabled
            if (this.infinite_scroll) {
                this.hide_default_footer = true
            } else{
                this.hide_default_footer = undefined
            }
        },
        sortOrder() {
            // Creates a new object to send to the server so that it knows how to sort
            let sort_criteria = []
            this.options.sortBy.forEach((element, index) => {
                let matchingHeader = this.headers.find(possibleMatch => possibleMatch.value === element)
                if (matchingHeader && matchingHeader.sortable) {
                    if(this.request_payload_type === "spatie") {
                        sort_criteria.push((this.options.sortDesc[index] ? '-' : '') + matchingHeader.value)
                    } else {
                        sort_criteria.push({
                            column: matchingHeader.value,
                            dir: (this.options.sortDesc[index] ? 'DESC' : 'ASC')
                        })
                    }
                }
            })
            if(this.request_payload_type === "spatie") {
                return sort_criteria.join(',');
            } else {
                return sort_criteria
            }
        },
        tableState() {
            // This returns the payload that will be sent to the server when querying for data

            let payload = null
            switch (this.request_payload_type) {
                case "spatie":
                    payload = {
                        draw: this.draw,
                        page: {
                            number: this.options.page,
                            size:this.options.itemsPerPage
                        },
                        filter: {},
                        sort: this.sortOrder(),
                    }
                    if(!!this.search) {
                        payload.filter.search = this.search;
                    }
                    if(!!this.options_payload){
                        payload.options = this.options_payload
                    }
                    this.headers
                        .filter(header => !!(header?.search))
                        .forEach(header => payload.filter[header.value] = header.search)
                    if (!!this.appends) {
                        payload.append=this.appends
                    }

                    break;
                default:
                    payload = {
                        options: this.options,
                        headers: this.headers,
                        search: this.search,
                        draw: this.draw,
                        order: this.sortOrder(),
                    }
                    break
            }
            return payload
        },
        ajaxPrerequisiteCheck() {
            // Check for required values before sending an AJAX request
            if (!this.headers){
                throw 'Please make sure you provide headers'
            }
            if (this.is_server_side && !this.endpoint){
                throw 'Please make sure you provide endpoint'
            }
        },
        addDataToTable(data_to_add) {
            // Add data to the array that drives the data table

            // How we handle the response data depends on whether infinite scrolling is in effect
            if (!this.infinite_scroll) {
                // Empty data out of 'items' property before continuing
                this.clearList()
            }

            // Update summary list if there is one.
            if (data_to_add.summaryList){
                this.summary_list = data_to_add.summaryList
            }

            let update_was_successful = false
            let unique_field = this.$attrs['item-key'] || 'id'
            data_to_add.data.forEach(new_item => {
                if (!this.items_local?.find(existing_item => existing_item[unique_field] === new_item[unique_field])){
                    // Push the item into the list
                    this.items_local.push(new_item)
                    update_was_successful = true
                }
                else {
                    // This item is a duplicate, do not push it
                }
            })
            if (!this.infinite_scroll || !this.items_local.length){
                this.found_count = data_to_add.recordsFiltered
            } else {
                this.found_count += data_to_add.recordsFiltered
            }
            if (this.infinite_scroll){
                if (update_was_successful){
                    this.options.page++
                }
                else {
                    this.end_of_data = true
                }
            }
        },
        initAjaxSetup: function () {
            let best_guess = undefined

            if (this.is_ziggy_route) {
                this.ziggy_route = route(this.endpoint)
                best_guess = this.ziggy_route?.urlBuilder?.route?.methods[0]
                this.endpoint_uri = this.ziggy_route.url()
                this.endpoint_method_local = this.endpoint_method || best_guess || "POST"
                return true
            }

            this.endpoint_uri = this.endpoint
            this.endpoint_method_local = this.endpoint_method || "POST"
            return true
        },
        isInfiniteScrollOptionChange(newVal, oldVal) {
            if (typeof oldVal !== 'object') {
                return false
            }

            if (!Object.entries(oldVal).length){    // There probably won't be an oldVal on first load
                return false
            }

            // We need to modify newVal and oldVal, so we should do so non-destructively.  Modify clones instead
            let newVal2 = structuredClone(newVal)
            let oldVal2 = structuredClone(oldVal)
            delete newVal2.page
            delete oldVal2.page

            // If the clones without page# options are the same, then we know that regular options had not changed
            return this.infinite_scroll && (JSON.stringify(newVal2) !== JSON.stringify(oldVal2))
        },
        clearList() {
            // This technique should keep the existing array in place, but remove all the items from it -- important to know for binding
            this.items_local.splice(0, this.items_local.length)
        },
        debouncedSearch: _.debounce(function () {
            if (this.infinite_scroll){
                this.clearList()
            }
            this.options.page = 1       // Brings us back to page #1
            this.getData()              // Updates data to match new search criteria
        }, 666),

        debouncedScroll: _.debounce(function () {
            let bottomOfWindow = document.documentElement.scrollTop + window.innerHeight === document.documentElement.offsetHeight
            if (this.infinite_scroll && !this.end_of_data){
                // User is trying to scroll, and as far as we know, there is probably more data available for us on the server
                if (bottomOfWindow) {
                    this.getData()
                }
            } else if (this.infinite_scroll && this.end_of_data) {
                // User is trying to scroll, but we're pretty sure there is no more data available for them
                if (!bottomOfWindow){
                    // If we're not at the bottom of the window even though we're at the end of data, then the user is probably trying to scroll back up
                    this.end_of_data = false
                }
            }
        }, 10),
        async refreshList() {
            this.options.page = 1
            this.clearList()
            await this.getData()
        }
    },
    mounted() {
        this.checkForInfiniteScroll()
        this.initAjaxSetup()
        this.getData()
    },
    watch: {
        options: {  // Fetch new data whenever the options for the datatable are updated (this includes itemsPerPage, pageNumber, sort order, etc...)
            handler(newVal, oldVal) {
                if (newVal != oldVal) {
                    if (this.isInfiniteScrollOptionChange(newVal, oldVal)){
                        this.clearList()
                    }
                    this.getData()
                }
            },
            deep: true
        },
        headers: {  // Fetch new data whenever the options for the datatable are updated (this includes itemsPerPage, pageNumber, sort order, etc...)
            handler(newVal, oldVal) {
                if (newVal != oldVal) {
                    if (this.isInfiniteScrollOptionChange(newVal, oldVal)){
                        this.clearList()
                    }
                    this.getData()
                }
            },
            deep: true
        },
        items: {  // Update our local version of items whenever the incoming prop is updated
            handler(newVal, oldVal) {
                this.$set(this, 'items_local', newVal)
            },
            deep: true
        },
        search(newVal, oldVal) {  // Fetch new data whenever the options for the datatable are updated (this includes itemsPerPage, pageNumber, sort order, etc...)
            if (this.is_server_side) {
                this.debouncedSearch()
            }else {
                // I don't think we need to do anything
            }
        },
    },
}
</script>

<style scoped>

</style>
